<template>
    <main class="news-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/news" class="tbv2-crumb">{{ $t('news.page.title') }}</router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ heroTitle }}</span>
            </div>
        </portal>

        <header class="news-hero">
            <router-link to="/news" class="news-back">
                <i class="fal fa-arrow-left"></i>
                <span>{{ $t('common.back') }}</span>
            </router-link>
            <div class="news-hero-row">
                <div>
                    <h1 class="news-hero-title">{{ heroTitle }}</h1>
                    <div v-if="article" class="news-hero-meta">
                        <span
                            class="news-badge"
                            :class="article.status === 'published' ? 'news-badge--pub' : 'news-badge--draft'"
                        >
                            <i
                                :class="article.status === 'published' ? 'fal fa-check-circle' : 'fal fa-pen'"
                            ></i>
                            {{ $t(`news.status.${article.status || 'draft'}`) }}
                        </span>
                        <span v-if="article.published_at" class="news-hero-date">
                            {{ formatDate(article.published_at) }}
                        </span>
                    </div>
                </div>
            </div>
        </header>

        <div v-if="isLoading" class="news-loading">
            <div class="spinner-border text-primary" role="status"></div>
        </div>

        <NewsForm
            v-else-if="article"
            mode="edit"
            :initial="article"
            :categories="categoriesList"
            :busy-action="busyAction"
            :can-delete="canDelete"
            @submit="onSubmit"
            @cancel="goBack"
        />
    </main>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions, mapState } from 'vuex'
import NewsForm from '@/components/News/NewsForm.vue'

export default {
    name: 'NewsEdit',
    components: { NewsForm },
    data() {
        return {
            isLoading: true,
            busyAction: null
        }
    },
    computed: {
        ...mapState('news', {
            article: (state) => state.article,
            categories: (state) => state.categories
        }),
        articleId() {
            return this.$route.params.id
        },
        categoriesList() {
            if (!this.categories) return []
            return Array.isArray(this.categories)
                ? this.categories
                : this.categories.data || []
        },
        heroTitle() {
            if (!this.article) return this.$t('news.actions.update')
            const translations = this.article.translations || []
            const lang = this.$store.state.lang
            const match =
                translations.find((t) => t.locale === lang) ||
                translations.find((t) => t.locale === 'fr') ||
                translations[0]
            return (match && match.title) || `#${this.article.id}`
        },
        permissions() {
            return JSON.parse(localStorage.getItem('permissions') || '[]')
        },
        canDelete() {
            return this.permissions.includes('news.delete')
        }
    },
    created() {
        this.getCategories().catch(() => {})
        this.loadArticle()
    },
    methods: {
        ...mapActions('news', [
            'getArticle',
            'getCategories',
            'updateArticle',
            'deleteArticle',
            'publishArticle',
            'unpublishArticle'
        ]),
        goBack() {
            this.$router.push({ name: 'News' })
        },
        formatDate(iso) {
            return this.moment(iso)
                .locale(this.$store.state.lang)
                .format('Do MMMM YYYY')
        },
        async loadArticle() {
            this.isLoading = true
            try {
                await this.getArticle(this.articleId)
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
                this.$router.push({ name: 'News' })
            } finally {
                this.isLoading = false
            }
        },
        async onSubmit({ action, payload }) {
            if (this.busyAction) return
            this.busyAction = action
            try {
                if (action === 'update') {
                    await this.updateArticle({ id: this.articleId, payload })
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('news.actions.update')
                    })
                    await this.loadArticle()
                } else if (action === 'publish') {
                    await this.updateArticle({ id: this.articleId, payload })
                    await this.publishArticle(this.articleId)
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('news.actions.publish')
                    })
                    await this.loadArticle()
                } else if (action === 'unpublish') {
                    await this.unpublishArticle(this.articleId)
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('news.actions.unpublish')
                    })
                    await this.loadArticle()
                } else if (action === 'delete') {
                    const dialog = this.$bmvDialog
                    const ok =
                        dialog && typeof dialog.confirm === 'function'
                            ? await dialog.confirm({
                                  variant: 'danger',
                                  title: this.$t('news.actions.delete_confirm'),
                                  confirmLabel: this.$t('common.delete'),
                                  cancelLabel: this.$t('common.cancel')
                              })
                            : window.confirm(this.$t('news.actions.delete_confirm'))
                    if (!ok) return
                    await this.deleteArticle(this.articleId)
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('common.delete')
                    })
                    this.$router.push({ name: 'News' })
                }
            } catch (err) {
                this.$store.dispatch('dispatchError', err)
            } finally {
                this.busyAction = null
            }
        }
    }
}
</script>

<style scoped>
.news-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.news-hero {
    margin-bottom: var(--v2-space-6);
}

.news-hero-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
}

.news-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 10px;
    transition: color var(--v2-transition-fast);
}

.news-back:hover {
    color: var(--v2-primary);
}

.news-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.2;
    word-break: break-word;
}

.news-hero-meta {
    display: inline-flex;
    align-items: center;
    gap: 10px;
}

.news-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 600;
    white-space: nowrap;
}

.news-badge--draft {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
}

.news-badge--pub {
    background: var(--v2-success-soft);
    color: var(--v2-success);
}

.news-hero-date {
    font-size: 0.78rem;
    color: var(--v2-text-3);
}

.news-loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 80px 0;
}

@media (max-width: 900px) {
    .news-v2 {
        padding: 16px 18px 32px;
    }
}
</style>
